import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  sizeCategoryIndexQueryKey,
  sizeCategoryShowOptions,
  sizeCategoryShowQueryKey,
  sizeCategoryUpdateMutation,
} from '$api-client/@tanstack/react-query.gen'
import { UpdateSizeCategoryForm, UpdateSizeCategorySchema } from '$features/settings/forms/size-category/update'
import { SettingsRoutePath } from '$features/settings/routes/protected'
import { Router } from '$utils/router'
import { NavigableModal, Text, LoaderIcon } from '$components'
import { ToastHelper, ToastType } from '$components'
import { QueryErrorHelper } from '$utils/query'
import { useNavigate, useParams } from 'react-router'

export function UpdateSizeCategoryModal() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { sizeCategoryId } = useParams()

  const handleClose = () => navigate(Router.buildRoute(SettingsRoutePath.Settings, SettingsRoutePath.SettingsPetOptionsSizes))

  const { data: sizeCategory, isLoading } = useQuery({
    ...sizeCategoryShowOptions({
      path: { sizeCategory: Number(sizeCategoryId) },
    }),
  })

  const { mutate, isPending, error } = useMutation({
    ...sizeCategoryUpdateMutation(),
    onSuccess: async () => {
      ToastHelper.updateSuccess(ToastType.SizeCategory)
      queryClient.invalidateQueries({ queryKey: sizeCategoryIndexQueryKey() })
      await queryClient.invalidateQueries({
        queryKey: sizeCategoryShowQueryKey({ path: { sizeCategory: Number(sizeCategoryId) } }),
      })
      handleClose()
    },
    onError: () => ToastHelper.updateError(ToastType.SizeCategory),
  })

  const handleSubmit = (values: UpdateSizeCategorySchema) => {
    mutate({
      path: { sizeCategory: Number(sizeCategoryId) },
      body: {
        name: values.name,
        from_weight_lbs: values.from_weight_lbs,
        to_weight_lbs: values.to_weight_lbs,
      },
    })
  }

  if (isLoading) {
    return (
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>Edit Size</NavigableModal.Heading>
        </NavigableModal.Header>
        <NavigableModal.Content>
          <LoaderIcon className="size-5 my-stack-hero mx-auto" />
        </NavigableModal.Content>
      </NavigableModal>
    )
  }

  if (!sizeCategory) {
    return (
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>Edit Size</NavigableModal.Heading>
        </NavigableModal.Header>
        <NavigableModal.Content className="flex items-center justify-center my-stack-hero">
          <Text type="s">Couldn&apos;t find size.</Text>
        </NavigableModal.Content>
      </NavigableModal>
    )
  }

  return (
    <UpdateSizeCategoryForm
      isLoading={isPending}
      onSubmit={handleSubmit}
      onCancel={handleClose}
      errors={QueryErrorHelper.getQueryValidationErrors(error)}
      defaultValues={{
        name: sizeCategory.data.values?.name,
        from_weight_lbs: Number(sizeCategory.data.values?.from_weight_lbs),
        to_weight_lbs: Number(sizeCategory.data.values?.to_weight_lbs),
      }}
    >
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>Edit Size</NavigableModal.Heading>
        </NavigableModal.Header>

        <NavigableModal.Content>
          <UpdateSizeCategoryForm.Content />
        </NavigableModal.Content>

        <NavigableModal.Footer>
          <UpdateSizeCategoryForm.Controls />
        </NavigableModal.Footer>
      </NavigableModal>
    </UpdateSizeCategoryForm>
  )
}
